import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import _, { isNumber } from 'lodash';

import Loading from '../shared/Loading';
import { SaveStatus } from '../shared/SavePanel';
import ChildPacksMedia from './ChildPacksMedia';
import { PackContent, PossibleContentFields } from './Pack/PackContent';
import {
    PackDescription,
    PossibleDescriptionFields,
} from './Pack/PackDescription';
import { PackDiscount } from './Pack/PackDiscount/PackDiscount';
import {
    getPackExportReadinessStatus,
    PackExport,
} from './Pack/PackExport/PackExport';
import PackMembership from './Pack/PackMembership/PackMembership';
import { PackOverview, PossibleOverviewFields } from './Pack/PackOverview';
import { PackPriceId } from './Pack/PackPriceId/PackPriceId';
import { PackScreenshotsWrapper } from './Pack/PackScreenshotsWrapper';
import { PackTypeSelector } from './Pack/PackTypeSelector';
import { XsollaVirtualItem } from './Pack/XsollaVirtualItem/XsollaVirtualItem';
import { PackPlaysets } from './PackPlaysets';
import { Modal, NewRow, SavePanel, Section } from '../shared';
import { LanguageType } from '@lifeserver/shared';

import { useOpenSections } from '../hooks/contexts';
import {
    type PackContentCounts,
    PackDescriptionType,
    useCreatePack,
    usePack,
    usePacks,
    useUpdatePack,
} from '../hooks/packs';

import { toHTMLDate, toJSONDate } from '../utils/dates';
import { ImageFileUploading, VideoFileUploading } from '../utils/upload';
import {
    computeSectionAttention,
    getPackDisplayName,
    handleError,
} from '../utils/utils';
import withRouter, { WithRouterProps } from '../utils/withRouter';
import { isPackBundle } from './utils';

import { RouteTo } from '../routes';

import { MediaType } from '../types/enums/mediatype.enum';
import { PackType } from '../types/enums/packtype.enum';
import { Store } from '../types/enums/store.enum';

import { Media } from '../types/mediaTypes';
import { Pack as PackT, PackUpdate, PriceLevel } from '../types/packTypes';

import { emptyPack } from './emptyPack';
import validate from './validate';

import lpppIcon from '../assets/lppp-logo.png';

type PackProps = {
    language: LanguageType;
} & WithRouterProps;

type MediaOrImageFileUploading = Media | (ImageFileUploading & { id?: number });
type MediaOrVideoFileUploading = Media | (VideoFileUploading & { id?: number });

type MediaState = {
    screenshots: MediaOrImageFileUploading[];
    videos: MediaOrVideoFileUploading[];
    thumbnails: MediaOrImageFileUploading[];
    widethumbnails: MediaOrImageFileUploading[];
    tabthumbnails: MediaOrImageFileUploading[];
    iapimages: MediaOrImageFileUploading[];
};

const mapMediasToIds = (medias: MediaState): PackUpdate['medias'] => {
    const mediaIds = _.flatten([
        medias.screenshots,
        medias.videos,
        medias.thumbnails,
        medias.widethumbnails,
        medias.tabthumbnails,
        medias.iapimages,
    ]).map(
        // Failed uploads are saved as part of the pack on purpose: uploads are
        // processed asynchronously, so keeping the reference is what makes the
        // failure survive reload.
        (media) => media.id as number,
    );

    return mediaIds;
};

export function Pack(props: PackProps) {
    const packId = props.params.id;
    const {
        pack: fetchedPack,
        fetch: fetchPack,
        isLoading: isPackLoading,
        error: packError,
        errorStatus: packErrorStatus,
    } = usePack(packId || '');
    const {
        packs: allPacks = [],
        fetch: fetchPacks,
        isLoading: isPacksLoading,
        error: packsError,
    } = usePacks();
    const {
        createPack,
        isLoading: isCreatePackLoading,
        pack: createdPack,
        error: createPackError,
    } = useCreatePack();
    const {
        updatePack,
        isLoading: isUpdatePackLoading,
        pack: updatedPack,
        error: updatePackError,
    } = useUpdatePack();

    const { openSections, toggleSection } = useOpenSections();

    const [pack, setPack] = useState<PackT>(emptyPack);
    const [media, setMedia] = useState<MediaState>({
        // all media lists moved here to transform to hooks easier as we are using dynamic state names :)
        screenshots: [],
        videos: [],
        thumbnails: [],
        widethumbnails: [],
        tabthumbnails: [],
        iapimages: [],
    });
    const [initialPack, setInitialPack] = useState<PackT | null>(null);
    const [initialMedia, setInitialMedia] = useState<PackUpdate['medias']>([]);
    const [isSavedStatusShown, setIsSavedStatusShown] =
        useState<boolean>(false);
    const [failedMediaMessages, setFailedMediaMessages] = useState<
        Record<number, string>
    >({});

    const onMediaProcessingFailed = (mediaId: number, message: string) => {
        setFailedMediaMessages((prev) => ({ ...prev, [mediaId]: message }));
    };

    const onFailedMediaRemoved = (mediaId: number) => {
        setFailedMediaMessages((prev) => {
            if (!(mediaId in prev)) {
                return prev;
            }
            const next = { ...prev };
            delete next[mediaId];
            return next;
        });
    };

    const hasUnsavedChanges = useMemo(() => {
        return (
            !_.isEqual(mapMediasToIds(media), initialMedia) ||
            !_.isEqual(pack, initialPack)
        );
    }, [pack, initialPack, media, initialMedia]);

    const isLoading = isPackLoading || isPacksLoading;

    // A stale bookmark or shared link can point at a pack that no longer
    // exists. Rendering the editor with an empty pack would look like a new
    // pack, so those ids are sent back to the pack list instead.
    const packNotFound = Boolean(packId) && packErrorStatus === 404;

    useEffect(() => {
        (async () => {
            if (pack.type === PackType.MULTIPACK) {
                await fetchPacks();
            }

            if (initialPack?.type !== pack.type) {
                resetPackContent(pack.type);
            }
        })();
    }, [pack.type]);

    useEffect(() => {
        setHasUnsavedChanges(hasUnsavedChanges);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        // Executed on the component mount
        if (packId) {
            fetchPack();
        }

        return () => {
            // Executed on the component unmount
            setHasUnsavedChanges(false);
        };
    }, []);

    useEffect(() => {
        if (packNotFound) {
            props.navigate(RouteTo.Packs());
        }
    }, [packNotFound]);

    useEffect(() => {
        if (packError && !packNotFound) {
            handleError('Unable to load pack', packError);
        }

        if (packsError) {
            handleError('Unable to load all packs', packsError);
        }
    }, [packError, packsError, packNotFound]);

    useEffect(() => {
        if (createdPack) {
            props.navigate(RouteTo.PackEdit(createdPack.packId));

            const { pack, media } = processCurrentPackData(createdPack);

            setInitialData(pack, media);
        }
    }, [createdPack]);

    useEffect(() => {
        if (updatedPack) {
            const { pack, media } = processCurrentPackData(updatedPack);

            setInitialData(pack, media);
        }
    }, [updatedPack]);

    useEffect(() => {
        if (fetchedPack) {
            const { pack, media } = processCurrentPackData(fetchedPack);

            setPack(pack);
            setMedia(media);

            setInitialData(pack, media);
        }
    }, [fetchedPack]);

    useEffect(() => {
        if (createPackError || updatePackError) {
            handleError(
                'Unable to save changes',
                isNew() ? createPackError : updatePackError,
            );
        }
    }, [createPackError, updatePackError]);

    useEffect(() => {
        if (isSavedStatusShown) {
            setTimeout(() => {
                setIsSavedStatusShown(false);
            }, 2000);
        }
    }, [isSavedStatusShown]);

    const getSaveStatus = (): SaveStatus => {
        if (isCreatePackLoading || isUpdatePackLoading) {
            return SaveStatus.SAVING;
        }

        if (hasUnsavedChanges) {
            return SaveStatus.HAS_CHANGED;
        }

        return SaveStatus.NO_CHANGES;
    };

    const setInitialData = (pack: PackT, media: MediaState) => {
        setInitialPack(pack);
        setInitialMedia(mapMediasToIds(media));

        setHasUnsavedChanges(false);
    };

    const setHasUnsavedChanges = (hasUnsavedChanges: boolean) => {
        localStorage.setItem('hasUnsavedChanges', String(hasUnsavedChanges));
    };

    const processCurrentPackData = (pack: PackT) => {
        const screenshots = _.filter(pack.medias, {
            type: MediaType.SCREENSHOT,
        });
        const videos = _.filter(pack.medias, { type: MediaType.VIDEO });
        const thumbnails = _.filter(pack.medias, {
            type: MediaType.THUMBNAIL,
        });
        const widethumbnails = _.filter(pack.medias, {
            type: MediaType.WIDE_THUMBNAIL,
        });
        const tabthumbnails = _.filter(pack.medias, {
            type: MediaType.TAB_THUMBNAIL,
        });
        const iapimages = _.filter(pack.medias, {
            type: MediaType.IAP_IMAGE,
        });

        return {
            pack: {
                ...pack,
                medias: [],
                // todo Set defaults on server side
                displayName: pack.displayName || {},
                type: pack.type || PackType.UNKNOWN,
                childPacks: pack.childPacks || [],
                scenes: pack.scenes || [],
                content: pack.content || [],
                features: pack.features || {},
                tags: pack.tags || [],
                locations: pack.locations || 0,
                characters: pack.characters || 0,
                activeFromDate: toHTMLDate(pack.activeFromDate),
                activeToDate: toHTMLDate(pack.activeToDate),
            },
            media: {
                screenshots,
                videos,
                thumbnails,
                widethumbnails,
                tabthumbnails,
                iapimages,
            },
        };
    };

    const isNew = () => {
        return !props.params.id;
    };

    const onInputChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        language?: LanguageType | null,
    ) => {
        const name = e.target.name as
            | PossibleContentFields
            | PossibleDescriptionFields
            | PossibleOverviewFields;
        const value: string | number = e.target.value;
        const data = _.cloneDeep(pack);

        if (
            language &&
            (name === PossibleDescriptionFields.DisplayName ||
                name === PossibleDescriptionFields.Features)
        ) {
            data[name][language] = value;
        } else if (isNumber(data[name])) {
            (data[name] as number) = value ? Number(value) : NaN;
        } else {
            (data[name] as string) = value;
        }

        setPack(data);
    };

    const toggleContentCheckbox = (childPack: PackT) => {
        const childPackIds: string[] = _.cloneDeep(pack.childPacks);
        const childPacksData = getChildPacksData({
            parentPack: pack,
            allPacks: allPacks,
        });
        let screenshots = _.cloneDeep(media.screenshots);

        // Check if a child pack already exists in the parent's content
        const uncheckedChild = _.find(childPacksData, (item) => {
            return item.packId === childPack.packId;
        });

        if (!uncheckedChild) {
            // Child pack was added to bundle's content
            childPackIds.unshift(childPack.packId);
        } else {
            // Remove child pack from the state as it was deselected
            _.pull(childPackIds, uncheckedChild.packId);

            // Remove child's media from the state
            _.forEach(uncheckedChild.medias, (media) => {
                // Delete child's media from parent's screenshots
                screenshots = screenshots.filter(
                    (item) => item.id !== media.id,
                );
            });
        }

        setMedia((prevMedia) => ({ ...prevMedia, screenshots }));
        setPack((prevPack) => ({ ...prevPack, childPacks: childPackIds }));
    };

    const toggleScreenshot = (mediaId: Media['id']) => {
        const screenshots = _.cloneDeep(media.screenshots);

        const screenshot = screenshots.find((media) => media.id === mediaId);
        if (screenshot) {
            _.pull(screenshots, screenshot);
        } else {
            const childPacksData = getChildPacksData({
                parentPack: pack,
                allPacks: allPacks,
            });
            _.forEach(childPacksData, (childPack: PackT) => {
                const mediaInTheChild = childPack.medias.find(
                    (childMedia: Media) => childMedia.id === mediaId,
                );

                if (mediaInTheChild) {
                    screenshots.push(mediaInTheChild);
                    return;
                }
            });
        }

        setMedia((prevMedia) => ({ ...prevMedia, screenshots }));
    };

    const trimNameAndIdValues = (pack: PackT): PackT => {
        if (pack.name) pack.name = pack.name.trim();
        if (pack.packId) pack.packId = pack.packId.trim();

        return pack;
    };

    const onGoogleSheetDataLoaded = ({
        displayName,
        features,
    }: PackDescriptionType) => {
        setPack((prevPack) => ({ ...prevPack, displayName, features }));
    };

    const onContentPrefill = (content: Partial<PackContentCounts>) => {
        setPack((prevPack) => ({ ...prevPack, ...content }));
    };

    const getPackUpdateBody = (): PackUpdate => {
        return _.omit(
            {
                ...trimNameAndIdValues(_.cloneDeep(pack)),
                activeFromDate: toJSONDate(pack.activeFromDate),
                activeToDate: toJSONDate(pack.activeToDate),
                medias: mapMediasToIds(media),
            },
            'childrenTags',
            'id',
        );
    };

    const save = async () => {
        setHasUnsavedChanges(false);

        const body = getPackUpdateBody();
        if (isNew()) {
            await createPack(body);
        } else {
            await updatePack(body);
        }

        setIsSavedStatusShown(true);
    };

    const onScenesChange = (scenes: PackT['scenes']) => {
        setPack((prevPack) => ({ ...prevPack, scenes }));
    };

    const onContentChange = (content: PackT['content']) => {
        setPack((prevPack) => ({ ...prevPack, content }));
    };

    const onPriceLevelsChange = (priceLevels: PriceLevel[]) => {
        setPack((prevPack) => ({ ...prevPack, priceLevels }));
    };

    const onMediaChanged = (
        type: MediaType,
        updater: (
            prevItems: (
                | MediaOrImageFileUploading
                | MediaOrVideoFileUploading
            )[],
        ) => (MediaOrImageFileUploading | MediaOrVideoFileUploading)[],
    ) => {
        setMedia((prevMedia) => ({
            ...prevMedia,
            [type + 's']: updater(
                prevMedia[(type + 's') as keyof MediaState] as (
                    | MediaOrImageFileUploading
                    | MediaOrVideoFileUploading
                )[],
            ),
        }));
    };

    const deletePack = async (e: MouseEvent<HTMLButtonElement>) => {
        const isConfirmed = await Modal.confirm({
            heading: 'Delete pack',
            text: 'Really delete this pack?',
            okLabel: 'Yes do it!',
        });

        if (!isConfirmed) return;

        try {
            await axios.delete('/api/store/v2/pack/' + packId);

            props.navigate(RouteTo.Packs());
        } catch {
            handleError('Error deleting pack', e);
        }
    };

    const resetPackContent = (type: PackType) => {
        const data = _.cloneDeep(pack);

        data.outfits = 0;
        data.accessories = 0;
        data.hairStyles = 0;
        data.furniture = 0;
        data.templates = 0;
        data.surfaceDesigns = 0;
        data.garments = 0;
        data.patterns = 0;
        data.stickers = 0;
        data.characters = 0;
        data.locations = 0;
        data.childPacks = [];
        data.content = [];
        data.scenes = [];
        data.worth = type === PackType.MULTIPACK ? 0 : 1;

        setPack(data);
    };

    // Media are saved as ids only, so the export gate needs to look at the
    // client-side media state to know whether everything has finished
    // processing on the server (or failed and still needs removing).
    const hasUnprocessedMedias = () =>
        _.flatten(Object.values(media)).some(
            (item) =>
                'processingStatus' in item &&
                (item.processingStatus === 'error' ||
                    item.processingStatus === 'in_progress'),
        );

    const onExportReadinessCheck = () => {
        const childPacksData = getChildPacksData({
            parentPack: pack,
            allPacks: allPacks,
        });
        const validationResults = validate(getPackUpdateBody(), childPacksData);

        return getPackExportReadinessStatus({
            validationResults,
            hasUnsavedChanges,
            pack,
            hasUnprocessedMedias: hasUnprocessedMedias(),
        });
    };

    const getChildPacksData = _.memoize(
        ({
            parentPack,
            allPacks,
        }: {
            parentPack: PackT;
            allPacks: PackT[];
        }): PackT[] => {
            return allPacks.filter((pack: PackT) =>
                parentPack.childPacks.includes(pack.packId),
            );
        },
    );

    const renderChildPacksMedia = () => {
        if (pack.type !== PackType.MULTIPACK) {
            return null;
        }

        let selectedMediasCount = 0;
        const childPacksData = getChildPacksData({
            parentPack: pack,
            allPacks: allPacks,
        });
        media.screenshots.map((parentScreenshot) => {
            _.forEach(childPacksData, (pack) => {
                const mediaExistsInTheChild = pack.medias.find(
                    (childMedia: Media) =>
                        childMedia.id === parentScreenshot.id,
                );
                if (mediaExistsInTheChild) {
                    selectedMediasCount++;
                }
            });
        });

        const sectionTitle = `Select media from child packs ${
            selectedMediasCount > 0
                ? ' (' + selectedMediasCount + ' total)'
                : ''
        }`;
        return (
            <Section
                id="childPacksMedia"
                title={sectionTitle}
                titleIcon={lpppIcon}
                openSections={openSections}
                toggleSection={toggleSection}
            >
                {pack.childPacks.length > 0 && (
                    <ChildPacksMedia
                        isLoading={isLoading}
                        childPacksData={childPacksData}
                        parentScreenshots={media.screenshots}
                        toggleSelectedMedia={toggleScreenshot}
                    />
                )}
                {_.isEmpty(pack.childPacks) && (
                    <div>Packs from bundle's content will appear here</div>
                )}
            </Section>
        );
    };

    const renderDiscountSection = () => {
        if (pack.type === PackType.MULTIPACK) {
            return null;
        }

        return (
            <Section
                id="packDiscount"
                title="Discount period"
                openSections={openSections}
                toggleSection={toggleSection}
            >
                <PackDiscount sku={pack.priceLevels[0]?.productId || ''} />
            </Section>
        );
    };

    if (isLoading) return <Loading />;

    const isBundle = isPackBundle(pack);
    const childPacksData = getChildPacksData({
        parentPack: pack,
        allPacks: allPacks,
    });
    const validationResults = validate(getPackUpdateBody(), childPacksData);
    const sectionAttention = (fieldPaths: string[]) =>
        computeSectionAttention(validationResults, fieldPaths);
    const xsollaSkus = pack.priceLevels
        .flatMap((pl) => pl.platformData)
        .filter((pd) => pd.store === Store.XSOLLA)
        .map((pd) => pd.sku);

    return (
        <>
            <Section
                id="packOverview"
                title={
                    isNew()
                        ? `New pack: ${pack.name}`
                        : `Edit pack: ${getPackDisplayName(pack)}`
                }
                openSections={openSections}
                toggleSection={toggleSection}
                attention={sectionAttention([
                    'packId',
                    'name',
                    'requiredVersion',
                    'worth',
                    'priority',
                    'type',
                    'activeFromDate',
                    'activeToDate',
                    'tags',
                ])}
            >
                <PackTypeSelector
                    packType={pack.type}
                    onTypeChange={onInputChange}
                />
                <div style={{ width: '100%', height: '50px' }}></div>
                <PackOverview
                    pack={pack}
                    isNew={isNew()}
                    onInputChange={(e) => onInputChange(e)}
                    onTagsChange={(tags) =>
                        setPack((prevPack) => ({ ...prevPack, tags }))
                    }
                />
                <NewRow />
                <PackExport
                    pack={pack}
                    checkExportReadiness={onExportReadinessCheck}
                />
            </Section>

            {packId && (
                <Section
                    id="packMembership"
                    title={'Pack membership'}
                    openSections={openSections}
                    toggleSection={toggleSection}
                >
                    <PackMembership packId={packId} />
                </Section>
            )}

            <Section
                id="packContent"
                title="Pack content"
                openSections={openSections}
                toggleSection={toggleSection}
                attention={sectionAttention([
                    'content',
                    'scenes',
                    'childPacks',
                    'locations',
                    'characters',
                    'templates',
                    'furniture',
                    'surfaceDesigns',
                    'garments',
                    'patterns',
                    'stickers',
                    'outfits',
                    'accessories',
                    'hairStyles',
                ])}
            >
                {isBundle && (
                    <PackPlaysets
                        isLoading={isLoading}
                        childPackIds={pack.childPacks}
                        toggleContentCheckbox={toggleContentCheckbox}
                        allPacksData={allPacks}
                    />
                )}

                <PackContent
                    pack={pack}
                    onScenesChange={onScenesChange}
                    onContentChange={onContentChange}
                    onContentPrefill={onContentPrefill}
                    onInputChange={onInputChange}
                />
            </Section>

            <Section
                id="packDescription"
                title="Display name and description"
                openSections={openSections}
                toggleSection={toggleSection}
                attention={sectionAttention(['displayName', 'features'])}
            >
                <PackDescription
                    language={props.language}
                    pack={pack}
                    onGoogleSheetDataLoaded={onGoogleSheetDataLoaded}
                    onInputChange={(e) => onInputChange(e, props.language)}
                />
            </Section>

            <Section
                id="priceId"
                title="Set price ID"
                openSections={openSections}
                toggleSection={toggleSection}
                attention={sectionAttention([
                    'priceLevels',
                    'isBundleWorthCovered',
                ])}
            >
                <PackPriceId
                    pack={pack}
                    childPacks={getChildPacksData({
                        parentPack: pack,
                        allPacks,
                    })}
                    onPriceLevelsChange={onPriceLevelsChange}
                />
            </Section>

            {renderDiscountSection()}

            {renderChildPacksMedia()}

            <Section
                id="packImages"
                title={
                    isBundle
                        ? 'Arrange & Upload bundle media'
                        : 'Images and videos'
                }
                openSections={openSections}
                toggleSection={toggleSection}
                attention={
                    Object.keys(failedMediaMessages).length > 0
                        ? {
                              severity: 'error',
                              messages: Object.values(failedMediaMessages),
                          }
                        : sectionAttention(['medias'])
                }
            >
                <PackScreenshotsWrapper
                    pack={pack}
                    media={media}
                    childPacksData={childPacksData}
                    onMediaChanged={onMediaChanged}
                    onMediaProcessingFailed={onMediaProcessingFailed}
                    onFailedMediaRemoved={onFailedMediaRemoved}
                    toggleScreenshot={toggleScreenshot}
                />
            </Section>

            {xsollaSkus.length > 0 && (
                <Section
                    id="xsollaVirtualItem"
                    title="Xsolla Virtual Item"
                    openSections={openSections}
                    toggleSection={toggleSection}
                    style={{ marginBottom: 50 }}
                >
                    <XsollaVirtualItem skus={xsollaSkus} />
                </Section>
            )}

            <SavePanel
                title={'Working with: ' + getPackDisplayName(pack)}
                validationResults={validationResults}
                item={pack}
                save={save}
                showDelete={!isNew()}
                saveStatus={
                    isSavedStatusShown ? SaveStatus.SAVED : getSaveStatus()
                }
                onDelete={deletePack}
            />
        </>
    );
}

export default withRouter(Pack);
